import {
  createRouter,
  createRoute,
  createRootRoute,
  Outlet,
} from '@tanstack/react-router';
import { MainLayout } from '../shared/layouts/MainLayout';
import { AuthLayout } from '../shared/layouts/AuthLayout';
import { ProtectedRoute } from '../shared/auth/ProtectedRoute';
import { LandingPage } from '../features/landing/LandingPage';
import { LoginPage } from '../features/auth/LoginPage';
import { RegisterPage } from '../features/auth/RegisterPage';
import { SetupMfaPage } from '../features/auth/SetupMfaPage';
import { DashboardPage } from '../features/dashboard/DashboardPage';
import { PerformanceGlobalPage } from '../features/dashboard/PerformanceGlobalPage';
import { SalesListPage } from '../features/sales/SalesListPage';
import { SalesCheckoutPage } from '../features/sales/SalesCheckoutPage';
import { SalesFormPage } from '../features/sales/SalesFormPage';
import { PosSalePage } from '../features/pos/PosSalePage';
import { PosSessionPage } from '../features/pos/PosSessionPage';
import { PosPage } from '../features/pos/PosPage';
import { StockPage } from '../features/inventory/StockPage';
import { FinanceDashboardPage } from '../features/finance/FinanceDashboardPage';
import { ProductionOrderPage } from '../features/manufacturing/ProductionOrderPage';
import { FiscalInvoiceListPage } from '../features/fiscal/FiscalInvoiceListPage';
import { TaxRatesPage } from '../features/fiscal/tax-rates/TaxRatesPage';
import { TaxRulesPage } from '../features/fiscal/tax-rules/TaxRulesPage';
import { TaxProfilesPage } from '../features/fiscal/tax-profiles/TaxProfilesPage';
import { FiscalInvoiceEmitPage } from '../features/fiscal/FiscalInvoiceEmitPage';
import { FiscalInvoiceDetailPage } from '../features/fiscal/FiscalInvoiceDetailPage';
import { CfopTablePage } from '../features/fiscal/cfop/CfopTablePage';
import { CstTablesPage } from '../features/fiscal/cst/CstTablesPage';
import { NcmTablePage } from '../features/fiscal/ncm/NcmTablePage';
import { TaxReformPage } from '../features/fiscal/reform/TaxReformPage';
import { TaxSimulatorPage } from '../features/fiscal/tax-calculate/TaxSimulatorPage';
import { PartiesPage } from '../features/master-data/PartiesPage';
import { ProductsPage } from '../features/master-data/ProductsPage';
import { ProductFormPage } from '../features/master-data/ProductFormPage';
import { ProcurementRequestsPage } from '../features/procurement/ProcurementRequestsPage';
import { SupportTicketsPage } from '../features/support/SupportTicketsPage';
import { AccountingPage } from '../features/accounting/AccountingPage';
import { AuditPage } from '../features/audit/AuditPage';
import { DocumentsPage } from '../features/documents/DocumentsPage';
import { IntegrationPage } from '../features/integration/IntegrationPage';
import { UsersPage } from '../features/iam/UsersPage';
import { RolesPage } from '../features/iam/RolesPage';
import { SessionsPage } from '../features/iam/SessionsPage';
import { MembershipsPage } from '../features/iam/MembershipsPage';
import { AccessRequestsPage } from '../features/iam/AccessRequestsPage';
import { ServicesPage } from '../features/master-data/ServicesPage';
import { CategoriesPage } from '../features/master-data/CategoriesPage';
import { CustomersPage } from '../features/master-data/CustomersPage';
import { QuotesPage } from '../features/sales/QuotesPage';
import { PipelinePage } from '../features/crm/PipelinePage';
import { FunnelPage } from '../features/crm/FunnelPage';
import { NewOpportunityPage } from '../features/crm/NewOpportunityPage';
import { CustomerFormPage } from '../features/crm/CustomerFormPage';
import { CustomerFichaPage } from '../features/crm/CustomerFichaPage';
import { CommissionsPage } from '../features/crm/CommissionsPage';
import { PurchaseOrdersPage } from '../features/procurement/PurchaseOrdersPage';
import { RfqListPage } from '../features/procurement/RfqListPage';
import { GoodsReceiptsPage } from '../features/procurement/GoodsReceiptsPage';
import { OnboardingSetupWizardPage } from '../features/onboarding/OnboardingSetupWizardPage';
import { WarehousesPage } from '../features/inventory/WarehousesPage';
import { TrackingPage } from '../features/inventory/TrackingPage';
import { MovementsPage } from '../features/inventory/MovementsPage';
import { CountsPage } from '../features/inventory/CountsPage';
import { ReservationsPage } from '../features/inventory/ReservationsPage';
import { TerminalsPage } from '../features/pos/TerminalsPage';
import { PayablesPage } from '../features/finance/PayablesPage';
import { ReceivablesPage } from '../features/finance/ReceivablesPage';
import { BankAccountsPage } from '../features/finance/BankAccountsPage';
import { ReconciliationPage } from '../features/finance/ReconciliationPage';
import { ChartOfAccountsPage } from '../features/accounting/ChartOfAccountsPage';
import { JournalPage } from '../features/accounting/JournalPage';
import { FinancialStatementsPage } from '../features/accounting/FinancialStatementsPage';
import { IntegrationsListPage } from '../features/integration/IntegrationsListPage';
import { IntegrationExportsPage } from '../features/integration/IntegrationExportsPage';
import { MasterDataImportPage } from '../features/master-data/MasterDataImportPage';
import { IntegrationImportsPage } from '../features/integration/IntegrationImportsPage';
import { IntegrationWebhooksPage } from '../features/integration/IntegrationWebhooksPage';
import { BomsPage } from '../features/manufacturing/BomsPage';
import { CompaniesPage } from '../features/organization/CompaniesPage';
import { BranchesPage } from '../features/organization/BranchesPage';
import { NumberSequencesPage } from '../features/organization/NumberSequencesPage';
import { OrganizationPage } from '../features/organization/OrganizationPage';
import { ErrorLogsPage } from '../features/observability/ErrorLogsPage';
import { FeatureFlagRoute } from '../shared/flags/FeatureFlagRoute';
import { PayablesDashboardPage } from '../features/finance/PayablesDashboardPage';
import { PayableWriteOffPage } from '../features/finance/PayableWriteOffPage';
import { ReceivablesDashboardPage } from '../features/finance/ReceivablesDashboardPage';
import { ReceivableWriteOffPage } from '../features/finance/ReceivableWriteOffPage';
import { TreasuryLedgerPage } from '../features/finance/TreasuryLedgerPage';
import { PaymentConfirmPage } from '../features/finance/PaymentConfirmPage';
import { PaymentTermsPage } from '../features/finance/PaymentTermsPage';
import { BankTariffsPage } from '../features/finance/treasury/BankTariffsPage';
import { BankTariffsCardPage } from '../features/finance/treasury/BankTariffsCardPage';
import { BankTariffsPixPage } from '../features/finance/treasury/BankTariffsPixPage';
import { BankTariffsTransferPage } from '../features/finance/treasury/BankTariffsTransferPage';
import { DailyCashFlowPage } from '../features/finance/treasury/DailyCashFlowPage';
import { MdrRatesPage } from '../features/finance/treasury/MdrRatesPage';
import { PaymentsCnabPage } from '../features/finance/treasury/PaymentsCnabPage';
import { DreGerencialPage } from '../features/accounting/DreGerencialPage';
import { DreWaterfallPage } from '../features/accounting/DreWaterfallPage';
import { TrialBalancePage } from '../features/accounting/TrialBalancePage';
import { RevenueRecognitionPage } from '../features/accounting/RevenueRecognitionPage';
import { FiscalDashboardPage } from '../features/fiscal/dashboard/FiscalDashboardPage';
import { AssessmentReportPage } from '../features/fiscal/assessment-report/AssessmentReportPage';
import { AccrualDetailPage } from '../features/fiscal/accrual/AccrualDetailPage';
import { IbsCbsPage } from '../features/fiscal/ibs-cbs/IbsCbsPage';
import { RegimeSimulatorPage } from '../features/fiscal/regime-simulator/RegimeSimulatorPage';
import { TaxVariablesPage } from '../features/fiscal/tax-variables/TaxVariablesPage';
import { FreightDifalPage } from '../features/fiscal/freight-difal/FreightDifalPage';
import { UfExceptionMatrixPage } from '../features/fiscal/uf-matrix/UfExceptionMatrixPage';
import { NfceDocumentsPage } from '../features/fiscal/documents/NfceDocumentsPage';
import { NfseDocumentsPage } from '../features/fiscal/documents/NfseDocumentsPage';
import { NfsePipelinePage } from '../features/fiscal/documents/NfsePipelinePage';
import { ProductMappingPage } from '../features/fiscal/documents/ProductMappingPage';
import { FiscalEventsPage } from '../features/fiscal/documents/FiscalEventsPage';
import { ProductionOrderDetailPage } from '../features/manufacturing/ProductionOrderDetailPage';
import { ShopFloorPage } from '../features/manufacturing/ShopFloorPage';
import { BomLaborPage } from '../features/manufacturing/BomLaborPage';
import { BomCostsPage } from '../features/manufacturing/BomCostsPage';
import { BomAttachmentsPage } from '../features/manufacturing/BomAttachmentsPage';
import { BomRevisionsPage } from '../features/manufacturing/BomRevisionsPage';
import { ServiceOrdersPage } from '../features/manufacturing/ServiceOrdersPage';
import { ServiceOrderDetailPage } from '../features/manufacturing/ServiceOrderDetailPage';
import { ServiceOrderTimeEntriesPage } from '../features/manufacturing/ServiceOrderTimeEntriesPage';
import { ServiceOrderMaterialsPage } from '../features/manufacturing/ServiceOrderMaterialsPage';
import { ServiceOrderCommentsPage } from '../features/manufacturing/ServiceOrderCommentsPage';
import { SalesOrderApprovalPage } from '../features/sales/SalesOrderApprovalPage';
import { SalesOrderFinFiscalPage } from '../features/sales/SalesOrderFinFiscalPage';
import { ContractDetailPage } from '../features/sales/contracts/ContractDetailPage';
import { ContractRepositoryPage } from '../features/sales/contracts/ContractRepositoryPage';
import { ApprovalCenterPage } from '../features/sales/contracts/ApprovalCenterPage';
import { ContractBillingSchedulePage } from '../features/sales/contracts/ContractBillingSchedulePage';
import { ContractRenewalsPage } from '../features/sales/contracts/ContractRenewalsPage';
import { ApprovalRulesPage } from '../features/sales/contracts/ApprovalRulesPage';
import { ContractFinanceDashboardPage } from '../features/sales/contracts/ContractFinanceDashboardPage';
import { CtesPage } from '../features/logistics/CtesPage';
import { NewCtePage } from '../features/logistics/NewCtePage';
import { DeliveriesMonitorPage } from '../features/logistics/DeliveriesMonitorPage';
import { FreightTablesPage } from '../features/logistics/FreightTablesPage';
import { MdfesPage } from '../features/logistics/MdfesPage';
import { ObligationsDashboardPage } from '../features/obligations/ObligationsDashboardPage';
import { GuiasPage } from '../features/obligations/GuiasPage';
import { SpedWizardPage } from '../features/obligations/SpedWizardPage';
import { TransmissionsPage } from '../features/obligations/TransmissionsPage';
import { ClosingDashboardPage } from '../features/closing/ClosingDashboardPage';
import { ClosingChecklistPage } from '../features/closing/ClosingChecklistPage';
import { ClosingValidateSignPage } from '../features/closing/ClosingValidateSignPage';
import { ClosingReconciliationPage } from '../features/closing/ClosingReconciliationPage';
import { ContadorPortalPage } from '../features/closing/ContadorPortalPage';
import { OkrsPage } from '../features/okrs/OkrsPage';
import { DepartmentalGoalsPage } from '../features/okrs/DepartmentalGoalsPage';
import { ProductivityPage } from '../features/okrs/ProductivityPage';
import { ProductivityComparativoPage } from '../features/okrs/ProductivityComparativoPage';
import { RetentionRulesPage } from '../features/retention/RetentionRulesPage';
import { RetentionExceptionsPage } from '../features/retention/RetentionExceptionsPage';
import { RetentionMonitorPage } from '../features/retention/RetentionMonitorPage';

// Root route
const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

// Auth layout group
const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'auth',
  component: () => (
    <AuthLayout>
      <Outlet />
    </AuthLayout>
  ),
});

const loginRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/login',
  component: LoginPage,
});

const setupMfaRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/setup-mfa',
  component: SetupMfaPage,
});

const registerRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/register',
  component: RegisterPage,
});

// Landing route (public, no auth)
const landingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: LandingPage,
});

// Protected layout group
const protectedLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  component: () => (
    <ProtectedRoute>
      <MainLayout>
        <Outlet />
      </MainLayout>
    </ProtectedRoute>
  ),
});

const dashboardRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/dashboard',
  component: DashboardPage,
});

const performanceGlobalRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/dashboard/performance',
  component: PerformanceGlobalPage,
});

const salesListRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/sales',
  component: SalesListPage,
});

const salesCheckoutRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/sales/checkout',
  component: SalesCheckoutPage,
});

const salesNewRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/sales/new',
  component: SalesFormPage,
});

const salesEditRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/sales/$saleId/edit',
  component: SalesFormPage,
});

const posSaleRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/pos',
  component: () => (
    <FeatureFlagRoute flag="offline-pos">
      <PosPage />
    </FeatureFlagRoute>
  ),
});

const posSaleSaleRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/pos/sale',
  component: () => (
    <FeatureFlagRoute flag="offline-pos">
      <PosSalePage />
    </FeatureFlagRoute>
  ),
});

const posSessionRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/pos/session',
  component: () => (
    <FeatureFlagRoute flag="offline-pos">
      <PosSessionPage />
    </FeatureFlagRoute>
  ),
});

const stockRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/inventory/stock',
  component: StockPage,
});

const financeRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/finance',
  component: () => (
    <FinanceDashboardPage />
  ),
});

const manufacturingRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/manufacturing/production-orders',
  component: () => (
    <FeatureFlagRoute flag="manufacturing">
      <ProductionOrderPage />
    </FeatureFlagRoute>
  ),
});

const fiscalRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/fiscal/invoices',
  component: FiscalInvoiceListPage,
});

const fiscalTaxRatesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/fiscal/tax-rates',
  component: TaxRatesPage,
});

const fiscalTaxRulesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/fiscal/tax-rules',
  component: TaxRulesPage,
});

const fiscalTaxProfilesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/fiscal/tax-profiles',
  component: TaxProfilesPage,
});

const fiscalInvoiceEmitRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/fiscal/invoices/new',
  component: FiscalInvoiceEmitPage,
});

const fiscalInvoiceDetailRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/fiscal/invoices/$id',
  component: FiscalInvoiceDetailPage,
});

const fiscalCfopRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/fiscal/cfop-table',
  component: CfopTablePage,
});

const fiscalCstRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/fiscal/cst-tables',
  component: CstTablesPage,
});

const fiscalNcmRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/fiscal/ncm-table',
  component: NcmTablePage,
});

const fiscalReformRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/fiscal/tax-reform',
  component: TaxReformPage,
});

const fiscalTaxCalculateRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/fiscal/tax-calculate',
  component: TaxSimulatorPage,
});

const partiesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/master-data/parties',
  component: PartiesPage,
});

const productsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/master-data/products',
  component: ProductsPage,
});

const productNewRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/master-data/products/new',
  component: ProductFormPage,
});

const masterDataImportRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/master-data/import',
  component: MasterDataImportPage,
});

const procurementRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/procurement/requests',
  component: ProcurementRequestsPage,
});

const supportRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/support/tickets',
  component: SupportTicketsPage,
});

// ── Módulos adicionais ────────────────────────────────────────────────

const accountingRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/accounting',
  component: AccountingPage,
});

const auditRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/audit',
  component: AuditPage,
});

const documentsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/documents',
  component: DocumentsPage,
});

const integrationRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/integrations',
  component: IntegrationPage,
});

const usersRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/iam/users',
  component: UsersPage,
});

const rolesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/iam/roles',
  component: RolesPage,
});

const organizationRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/organization',
  component: OrganizationPage,
});

const companiesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/organization/companies',
  component: CompaniesPage,
});

const branchesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/organization/branches',
  component: BranchesPage,
});

const numberSequencesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/organization/number-sequences',
  component: NumberSequencesPage,
});

const sessionsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/iam/sessions',
  component: SessionsPage,
});

const membershipsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/iam/memberships',
  component: MembershipsPage,
});

const accessRequestsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/iam/access-requests',
  component: AccessRequestsPage,
});

const servicesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/master-data/services',
  component: ServicesPage,
});

const categoriesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/master-data/categories',
  component: CategoriesPage,
});

const customersRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/master-data/customers',
  component: CustomersPage,
});

const customerNewRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/master-data/customers/new',
  component: CustomerFormPage,
});

const customerFichaIndexRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/crm/customers',
  component: CustomerFichaPage,
});

const customerFichaRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/crm/customers/$customerId',
  component: CustomerFichaPage,
});

const crmPipelineRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/crm/pipeline',
  component: PipelinePage,
});

const crmFunnelRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/crm/funnel',
  component: FunnelPage,
});

const crmOpportunityNewRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/crm/opportunities/new',
  component: NewOpportunityPage,
});

const crmCommissionsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/crm/commissions',
  component: CommissionsPage,
});

const quotesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/sales/quotes',
  component: QuotesPage,
});

const purchaseOrdersRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/procurement/orders',
  component: PurchaseOrdersPage,
});

const rfqListRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/procurement/rfqs',
  component: RfqListPage,
});

const goodsReceiptsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/procurement/receipts',
  component: GoodsReceiptsPage,
});

const onboardingRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/onboarding/setup',
  component: OnboardingSetupWizardPage,
});

const warehousesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/inventory/warehouses',
  component: WarehousesPage,
});

const trackingRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/inventory/tracking',
  component: TrackingPage,
});

const movementsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/inventory/movements',
  component: MovementsPage,
});

const countsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/inventory/counts',
  component: CountsPage,
});

const reservationsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/inventory/reservations',
  component: ReservationsPage,
});

const terminalsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/pos/terminals',
  component: TerminalsPage,
});

const payablesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/finance/payables',
  component: () => (
    <PayablesPage />
  ),
});

const receivablesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/finance/receivables',
  component: () => (
    <ReceivablesPage />
  ),
});

const bankAccountsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/finance/accounts',
  component: () => (
    <BankAccountsPage />
  ),
});

const reconciliationRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/finance/reconciliation',
  component: () => (
    <ReconciliationPage />
  ),
});

const chartOfAccountsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/accounting/chart',
  component: () => (
    <ChartOfAccountsPage />
  ),
});

const journalRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/accounting/journal',
  component: () => (
    <JournalPage />
  ),
});

const financialStatementsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/accounting/statements',
  component: () => (
    <FinancialStatementsPage />
  ),
});

const integrationsListRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/integrations/applications',
  component: IntegrationsListPage,
});

const integrationExportsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/integrations/exports',
  component: IntegrationExportsPage,
});

const integrationImportsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/integrations/imports',
  component: IntegrationImportsPage,
});

const integrationWebhooksRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/integrations/webhooks',
  component: IntegrationWebhooksPage,
});

const bomsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/manufacturing/boms',
  component: () => (
    <FeatureFlagRoute flag="manufacturing">
      <BomsPage />
    </FeatureFlagRoute>
  ),
});

const errorLogsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/admin/logs',
  component: ErrorLogsPage,
});

// Register routes
const payablesDashboardRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/finance/payables-dashboard',
  component: PayablesDashboardPage,
});

const payableWriteOffRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/finance/payables/$id/write-off',
  component: PayableWriteOffPage,
});

const receivablesDashboardRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/finance/receivables-dashboard',
  component: ReceivablesDashboardPage,
});

const receivableWriteOffRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/finance/receivables/$id/write-off',
  component: ReceivableWriteOffPage,
});

const treasuryLedgerRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/finance/ledger',
  component: TreasuryLedgerPage,
});

const paymentConfirmRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/finance/payments/confirm',
  component: PaymentConfirmPage,
});

const paymentTermsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/finance/payment-terms',
  component: PaymentTermsPage,
});

const bankTariffsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/finance/tariffs',
  component: BankTariffsPage,
});

const bankTariffsCardRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/finance/tariffs/cards',
  component: BankTariffsCardPage,
});

const bankTariffsPixRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/finance/tariffs/pix',
  component: BankTariffsPixPage,
});

const bankTariffsTransferRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/finance/tariffs/transfers',
  component: BankTariffsTransferPage,
});

const dailyCashFlowRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/finance/cash-flow',
  component: DailyCashFlowPage,
});

const mdrRatesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/finance/mdr-rates',
  component: MdrRatesPage,
});

const paymentsCnabRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/finance/payments-cnab',
  component: PaymentsCnabPage,
});

const dreGerencialRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/accounting/dre',
  component: DreGerencialPage,
});

const dreWaterfallRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/accounting/dre/waterfall',
  component: DreWaterfallPage,
});

const trialBalanceRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/accounting/trial-balance',
  component: TrialBalancePage,
});

const revenueRecognitionRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/accounting/revenue-recognition',
  component: RevenueRecognitionPage,
});

const fiscalDashboardRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/fiscal/dashboard',
  component: FiscalDashboardPage,
});

const assessmentReportRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/fiscal/assessment-report',
  component: AssessmentReportPage,
});

const accrualDetailRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/fiscal/accrual',
  component: AccrualDetailPage,
});

const ibsCbsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/fiscal/ibs-cbs',
  component: IbsCbsPage,
});

const regimeSimulatorRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/fiscal/regime-simulator',
  component: RegimeSimulatorPage,
});

const taxVariablesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/fiscal/tax-variables',
  component: TaxVariablesPage,
});

const freightDifalRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/fiscal/freight-difal',
  component: FreightDifalPage,
});

const ufExceptionMatrixRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/fiscal/uf-matrix',
  component: UfExceptionMatrixPage,
});

const nfceDocumentsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/fiscal/documents/nfce',
  component: NfceDocumentsPage,
});

const nfseDocumentsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/fiscal/documents/nfse',
  component: NfseDocumentsPage,
});

const nfsePipelineRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/fiscal/documents/nfse-pipeline',
  component: NfsePipelinePage,
});

const productMappingRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/fiscal/documents/product-mapping',
  component: ProductMappingPage,
});

const fiscalEventsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/fiscal/documents/events',
  component: FiscalEventsPage,
});

const productionOrderDetailRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/manufacturing/orders/$id',
  component: ProductionOrderDetailPage,
});

const shopFloorRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/manufacturing/shop-floor',
  component: ShopFloorPage,
});

const bomLaborRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/manufacturing/boms/$id/labor',
  component: BomLaborPage,
});

const bomCostsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/manufacturing/boms/$id/costs',
  component: BomCostsPage,
});

const bomAttachmentsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/manufacturing/boms/$id/attachments',
  component: BomAttachmentsPage,
});

const bomRevisionsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/manufacturing/boms/$id/revisions',
  component: BomRevisionsPage,
});

const serviceOrdersRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/manufacturing/service-orders',
  component: ServiceOrdersPage,
});

const serviceOrderDetailRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/manufacturing/service-orders/$id',
  component: ServiceOrderDetailPage,
});

const serviceOrderTimeEntriesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/manufacturing/service-orders/$id/time-entries',
  component: ServiceOrderTimeEntriesPage,
});

const serviceOrderMaterialsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/manufacturing/service-orders/$id/materials',
  component: ServiceOrderMaterialsPage,
});

const serviceOrderCommentsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/manufacturing/service-orders/$id/comments',
  component: ServiceOrderCommentsPage,
});

const salesOrderApprovalRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/sales/orders/$id/approval',
  component: SalesOrderApprovalPage,
});

const salesOrderFinFiscalRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/sales/orders/$id/fin-fiscal',
  component: SalesOrderFinFiscalPage,
});

const contractRepositoryRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/contracts',
  component: ContractRepositoryPage,
});

const contractDetailRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/contracts/$id',
  component: ContractDetailPage,
});

const contractBillingScheduleRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/contracts/$id/billing-schedule',
  component: ContractBillingSchedulePage,
});

const contractRenewalsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/contracts/renewals',
  component: ContractRenewalsPage,
});

const approvalCenterRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/approvals',
  component: ApprovalCenterPage,
});

const approvalRulesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/approval-rules',
  component: ApprovalRulesPage,
});

const contractFinanceDashboardRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/contracts/integration-dashboard',
  component: ContractFinanceDashboardPage,
});

const ctesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/logistics/ctes',
  component: CtesPage,
});

const newCteRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/logistics/ctes/new',
  component: NewCtePage,
});

const deliveriesMonitorRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/logistics/deliveries',
  component: DeliveriesMonitorPage,
});

const freightTablesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/logistics/freight-tables',
  component: FreightTablesPage,
});

const mdfesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/logistics/mdfes',
  component: MdfesPage,
});

const obligationsDashboardRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/obligations',
  component: ObligationsDashboardPage,
});

const guiasRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/obligations/guias',
  component: GuiasPage,
});

const spedWizardRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/obligations/sped',
  component: SpedWizardPage,
});

const transmissionsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/obligations/transmissions',
  component: TransmissionsPage,
});

const closingDashboardRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/closing',
  component: ClosingDashboardPage,
});

const closingChecklistRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/closing/periods/$id/checklist',
  component: ClosingChecklistPage,
});

const closingValidateSignRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/closing/periods/$id/validate-sign',
  component: ClosingValidateSignPage,
});

const closingReconciliationRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/closing/periods/$id/reconciliation',
  component: ClosingReconciliationPage,
});

const contadorPortalRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/closing/portal',
  component: ContadorPortalPage,
});

const okrsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/okrs',
  component: OkrsPage,
});

const departmentalGoalsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/okrs/goals',
  component: DepartmentalGoalsPage,
});

const productivityRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/okrs/productivity',
  component: ProductivityPage,
});

const productivityComparativoRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/okrs/productivity/comparative',
  component: ProductivityComparativoPage,
});

const retentionRulesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/retention/rules',
  component: RetentionRulesPage,
});

const retentionExceptionsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/retention/exceptions',
  component: RetentionExceptionsPage,
});

const retentionMonitorRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/retention/monitor',
  component: RetentionMonitorPage,
});

const routeTree = rootRoute.addChildren([
  landingRoute,
  authLayoutRoute.addChildren([loginRoute, setupMfaRoute, registerRoute]),
  protectedLayoutRoute.addChildren([
    dashboardRoute,
    performanceGlobalRoute,
    salesListRoute,
    salesCheckoutRoute,
    salesNewRoute,
    salesEditRoute,
    posSaleRoute,
    posSaleSaleRoute,
    posSessionRoute,
    stockRoute,
    financeRoute,
    manufacturingRoute,
    fiscalRoute,
    fiscalTaxRatesRoute,
    fiscalTaxRulesRoute,
    fiscalTaxProfilesRoute,
    fiscalInvoiceEmitRoute,
    fiscalInvoiceDetailRoute,
    fiscalCfopRoute,
    fiscalCstRoute,
    fiscalNcmRoute,
    fiscalReformRoute,
    fiscalTaxCalculateRoute,
    partiesRoute,
    productsRoute,
    productNewRoute,
    procurementRoute,
    supportRoute,
    accountingRoute,
    auditRoute,
    documentsRoute,
    integrationRoute,
    usersRoute,
    rolesRoute,
    organizationRoute,
    companiesRoute,
    branchesRoute,
    numberSequencesRoute,
    sessionsRoute,
    membershipsRoute,
    accessRequestsRoute,
    servicesRoute,
    categoriesRoute,
    customersRoute,
    customerNewRoute,
    customerFichaIndexRoute,
    customerFichaRoute,
    crmPipelineRoute,
    crmFunnelRoute,
    crmOpportunityNewRoute,
    crmCommissionsRoute,
    masterDataImportRoute,
    quotesRoute,
    purchaseOrdersRoute,
    rfqListRoute,
    goodsReceiptsRoute,
    warehousesRoute,
    trackingRoute,
    movementsRoute,
    countsRoute,
    reservationsRoute,
    terminalsRoute,
    payablesRoute,
    receivablesRoute,
    bankAccountsRoute,
    reconciliationRoute,
    chartOfAccountsRoute,
    journalRoute,
    financialStatementsRoute,
    integrationsListRoute,
    integrationExportsRoute,
    integrationImportsRoute,
    integrationWebhooksRoute,
    bomsRoute,
    onboardingRoute,
    errorLogsRoute,
    payablesDashboardRoute,
    payableWriteOffRoute,
    receivablesDashboardRoute,
    receivableWriteOffRoute,
    treasuryLedgerRoute,
    paymentConfirmRoute,
    paymentTermsRoute,
    bankTariffsRoute,
    bankTariffsCardRoute,
    bankTariffsPixRoute,
    bankTariffsTransferRoute,
    dailyCashFlowRoute,
    mdrRatesRoute,
    paymentsCnabRoute,
    dreGerencialRoute,
    dreWaterfallRoute,
    trialBalanceRoute,
    revenueRecognitionRoute,
    fiscalDashboardRoute,
    assessmentReportRoute,
    accrualDetailRoute,
    ibsCbsRoute,
    regimeSimulatorRoute,
    taxVariablesRoute,
    freightDifalRoute,
    ufExceptionMatrixRoute,
    nfceDocumentsRoute,
    nfseDocumentsRoute,
    nfsePipelineRoute,
    productMappingRoute,
    fiscalEventsRoute,
    productionOrderDetailRoute,
    shopFloorRoute,
    bomLaborRoute,
    bomCostsRoute,
    bomAttachmentsRoute,
    bomRevisionsRoute,
    serviceOrdersRoute,
    serviceOrderDetailRoute,
    serviceOrderTimeEntriesRoute,
    serviceOrderMaterialsRoute,
    serviceOrderCommentsRoute,
    salesOrderApprovalRoute,
    salesOrderFinFiscalRoute,
    contractRepositoryRoute,
    contractDetailRoute,
    contractBillingScheduleRoute,
    contractRenewalsRoute,
    approvalCenterRoute,
    approvalRulesRoute,
    contractFinanceDashboardRoute,
    ctesRoute,
    newCteRoute,
    deliveriesMonitorRoute,
    freightTablesRoute,
    mdfesRoute,
    obligationsDashboardRoute,
    guiasRoute,
    spedWizardRoute,
    transmissionsRoute,
    closingDashboardRoute,
    closingChecklistRoute,
    closingValidateSignRoute,
    closingReconciliationRoute,
    contadorPortalRoute,
    okrsRoute,
    departmentalGoalsRoute,
    productivityRoute,
    productivityComparativoRoute,
    retentionRulesRoute,
    retentionExceptionsRoute,
    retentionMonitorRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPendingComponent: () => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        color: '#43474e',
      }}
    >
      Carregando...
    </div>
  ),
  defaultErrorComponent: ({ error }) => (
    <div
      role="alert"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        color: '#ba1a1a',
      }}
    >
      <h1>Erro inesperado</h1>
      <p>{(error as Error).message}</p>
    </div>
  ),
  defaultNotFoundComponent: () => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        color: '#43474e',
      }}
    >
      <h1>404</h1>
      <p>Pagina não encontrada</p>
    </div>
  ),
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
